<template>
  <div class="onlyoffice-page">
    <!-- 顶部栏 -->
    <header class="oo-topbar">
      <div class="oo-topbar__left">
        <el-button size="small" icon="el-icon-back" @click="goBack">返回</el-button>
        <div class="oo-topbar__file">
          <i class="el-icon-document"></i>
          <span class="oo-topbar__name">{{ fileName }}</span>
        </div>
      </div>
      <div class="oo-topbar__right">
        <!-- 预览/编辑模式切换 -->
        <div class="oo-mode-switch">
          <button
            class="oo-mode-btn"
            :class="{ 'is-active': editorMode === 'view' }"
            @click="switchMode('view')"
          >
            <i class="el-icon-view"></i> 预览
          </button>
          <button
            class="oo-mode-btn"
            :class="{ 'is-active': editorMode === 'edit' }"
            @click="switchMode('edit')"
          >
            <i class="el-icon-edit-outline"></i> 编辑
          </button>
        </div>
        <!-- 协作用户名入口 -->
        <el-popover
          v-model="showNamePopover"
          placement="bottom-end"
          width="280"
          trigger="click"
        >
          <div class="oo-collab-form">
            <div class="oo-collab-form__title">协作名称</div>
            <el-input
              v-model="tempCollabName"
              size="small"
              placeholder="请输入协作名称"
              maxlength="20"
              clearable
              @keyup.enter.native="applyCollabName"
            />
            <div class="oo-collab-form__hint">此名称将显示在编辑器中，其他协作者可见</div>
            <div class="oo-collab-form__actions">
              <el-button size="mini" @click="showNamePopover = false">取消</el-button>
              <el-button size="mini" type="primary" @click="applyCollabName">确定</el-button>
            </div>
          </div>
          <div slot="reference" class="oo-topbar__user">
            <div class="oo-topbar__avatar">{{ collabName ? collabName.charAt(0) : '?' }}</div>
            <span class="oo-topbar__username">{{ collabName || '设置名称' }}</span>
            <i class="el-icon-setting oo-topbar__user-setting"></i>
          </div>
        </el-popover>
      </div>
    </header>

    <!-- 编辑器区域 -->
    <main class="oo-main">
      <!-- 加载状态 -->
      <div v-if="editorLoading" class="oo-loading">
        <div class="oo-loading__inner">
          <div class="oo-loading__icon">
            <i class="el-icon-document-checked"></i>
          </div>
          <div class="oo-loading__title">正在加载文档编辑器</div>
          <div class="oo-loading__desc">首次加载需下载编辑器资源，请耐心等待</div>
          <div class="oo-loading__progress">
            <div class="oo-loading__bar" :style="{ width: loadingPercent + '%' }"></div>
          </div>
          <div class="oo-loading__tips">
            <span>加载速度取决于网络环境</span>
            <el-button type="text" size="mini" @click="showSpeedTips = !showSpeedTips">
              优化建议 <i :class="showSpeedTips ? 'el-icon-arrow-up' : 'el-icon-arrow-down'" />
            </el-button>
          </div>
          <div v-if="showSpeedTips" class="oo-loading__tips-content">
            <p>1. 确保与 OnlyOffice 服务器（oo.aojuai.com）网络通畅</p>
            <p>2. 首次加载较慢，后续会使用浏览器缓存</p>
            <p>3. 预览模式加载资源少于编辑模式</p>
          </div>
        </div>
      </div>
      <!-- OnlyOffice 编辑器容器 -->
      <div id="oo-editor" class="oo-editor"></div>
    </main>
  </div>
</template>

<script>
import { getOnlyOfficeConfig } from '@/api/api'

// OnlyOffice Document Server 地址
const ONLYOFFICE_SERVER = 'http://oo.aojuai.com:9102'

export default {
  name: 'ReportOnlyOffice',
  data () {
    return {
      editorMode: 'view',
      editorLoading: true,
      loadingPercent: 0,
      showSpeedTips: false,
      docEditor: null,
      collabName: '',
      tempCollabName: '',
      showNamePopover: false,
      // 区分首次加载和模式切换：首次显示全屏遮罩，切换时不弹遮罩
      isFirstLoad: true
    }
  },
  computed: {
    fileUrl () { return this.$route.query.fileUrl || '' },
    fileName () { return this.$route.query.fileName || '文档.docx' }
  },
  created () {
    // 默认协作用户名：优先使用系统登录用户名（字段名：auName）
    const userInfo = this.$store.state.user.userInfo || {}
    const defaultName = userInfo.auName || userInfo.userName || ''
    this.collabName = defaultName
    this.tempCollabName = defaultName
  },
  mounted () {
    // 添加 preconnect 优化：提前建立与 OnlyOffice 服务器的连接
    this.addPreconnect()
    this.initEditor()
  },
  beforeDestroy () {
    this.destroyEditor()
  },
  methods: {
    // 添加 DNS 预解析和预连接，加速 OnlyOffice 资源加载
    addPreconnect () {
      const hints = [
        { rel: 'dns-prefetch', href: ONLYOFFICE_SERVER },
        { rel: 'preconnect', href: ONLYOFFICE_SERVER }
      ]
      hints.forEach(({ rel, href }) => {
        if (!document.querySelector(`link[rel="${rel}"][href="${href}"]`)) {
          const link = document.createElement('link')
          link.rel = rel
          link.href = href
          document.head.appendChild(link)
        }
      })
    },

    // 初始化编辑器
    async initEditor () {
      if (!this.fileUrl) {
        this.editorLoading = false
        return
      }

      // 仅首次加载时显示全屏遮罩和进度条
      if (this.isFirstLoad) {
        this.editorLoading = true
        this.startLoadingProgress()
      }

      try {
        // 1. 动态加载 OnlyOffice API 脚本（仅首次加载）
        await this.loadApiScript()
        if (this.isFirstLoad) this.loadingPercent = 40

        // 2. 每次都请求后端获取新配置（确保 document.key 唯一，避免版本冲突）
        const res = await getOnlyOfficeConfig({
          fileUrl: this.fileUrl,
          fileName: this.fileName,
          mode: this.editorMode
        })
        const config = res.data || res
        if (this.isFirstLoad) this.loadingPercent = 70

        // 3. 注入协作用户信息
        const userInfo = this.$store.state.user.userInfo || {}
        config.editorConfig.user = {
          id: String(userInfo.auId || userInfo.id || ''),
          name: this.collabName || '访客'
        }

        // 4. 覆盖当前模式
        config.editorConfig.mode = this.editorMode

        // 5. 开启自动保存 + 强制保存
        config.editorConfig.customization = config.editorConfig.customization || {}
        config.editorConfig.customization.autosave = true
        config.editorConfig.customization.forcesave = true

        // 6. 编辑器事件
        config.events = {
          onDocumentReady: () => {
            this.editorLoading = false
            this.loadingPercent = 100
            this.isFirstLoad = false
          },
          onError: (event) => {
            this.editorLoading = false
            console.error('OnlyOffice 编辑器错误:', event)
            this.$message.error('文档加载出错，请刷新重试')
          }
        }

        // 7. 销毁旧实例，创建新编辑器
        this.destroyEditor()
        this.docEditor = new window.DocsAPI.DocEditor('oo-editor', config)
      } catch (e) {
        this.editorLoading = false
        this.$message.error(e.message || '加载文档失败')
      }
    },

    // 动态加载 OnlyOffice API 脚本
    loadApiScript () {
      return new Promise((resolve, reject) => {
        if (window.DocsAPI) {
          resolve()
          return
        }
        const script = document.createElement('script')
        script.src = `${ONLYOFFICE_SERVER}/web-apps/apps/api/documents/api.js`;
        console.log('onlyoffice------',script.src);
        script.async = true
        script.onload = resolve
        script.onerror = () => reject(new Error('OnlyOffice 服务不可用，请检查网络'))
        document.head.appendChild(script)
      })
    },

    // 模拟加载进度条
    startLoadingProgress () {
      this.loadingPercent = 0
      this._loadingTimer = setInterval(() => {
        if (this.loadingPercent < 90) {
          this.loadingPercent += Math.random() * 8
        }
      }, 300)
    },

    // 切换预览/编辑模式
    switchMode (mode) {
      if (mode === this.editorMode) return
      this.editorMode = mode
      this.initEditor()
    },

    // 应用协作名称
    applyCollabName () {
      const name = this.tempCollabName.trim()
      if (!name) {
        this.$message.warning('请输入协作名称')
        return
      }
      this.collabName = name
      this.showNamePopover = false
      this.initEditor()
    },

    // 销毁编辑器实例
    destroyEditor () {
      if (this._loadingTimer) {
        clearInterval(this._loadingTimer)
        this._loadingTimer = null
      }
      if (this.docEditor) {
        try {
          this.docEditor.destroyEditor()
        } catch (e) { /* 忽略 */ }
        this.docEditor = null
      }
    },

    // 返回上一页
    goBack () {
      this.destroyEditor()
      this.$router.go(-1)
    }
  }
}
</script>

<style lang="scss" scoped>
.onlyoffice-page {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 2000;
  background: #f0f2f5;
  display: flex;
  flex-direction: column;
}

/* ========== 顶部栏 ========== */
.oo-topbar {
  height: 50px;
  background: #fff;
  border-bottom: 1px solid #e4e7ed;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  &__file {
    display: flex;
    align-items: center;
    gap: 6px;

    i { color: #4f7cff; font-size: 18px; }
  }

  &__name {
    font-size: 15px;
    font-weight: 600;
    color: #1e293b;
    max-width: 300px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__right {
    display: flex;
    align-items: center;
    gap: 16px;
  }
}

/* ========== 模式切换按钮 ========== */
.oo-mode-switch {
  display: flex;
  align-items: center;
  background: #f1f5f9;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.oo-mode-btn {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 16px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  i { font-size: 14px; }

  &:hover {
    color: #334155;
    background: rgba(255, 255, 255, 0.6);
  }

  &.is-active {
    background: #fff;
    color: #4f7cff;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.04);

    i { color: #4f7cff; }
  }
}

/* ========== 用户名区域 ========== */
.oo-topbar__user {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 12px 4px 4px;
  border-radius: 20px;
  background: #f8fafc;
  cursor: pointer;
  transition: all 0.2s;

  &:hover { background: #f1f5f9; }
}

.oo-topbar__avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: linear-gradient(135deg, #4f7cff, #6c5ce7);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
}

.oo-topbar__username {
  font-size: 13px;
  font-weight: 500;
  color: #334155;
  max-width: 100px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.oo-topbar__user-setting {
  font-size: 14px;
  color: #94a3b8;
  transition: color 0.2s;

  .oo-topbar__user:hover & { color: #4f7cff; }
}

/* ========== 编辑器主区域 ========== */
.oo-main {
  flex: 1;
  position: relative;
  overflow: hidden;
}

.oo-editor {
  width: 100%;
  height: 100%;
}

/* ========== 加载状态 ========== */
.oo-loading {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(240, 242, 245, 0.95);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;

  &__inner {
    text-align: center;
    max-width: 380px;
    padding: 40px;
  }

  &__icon {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    background: linear-gradient(135deg, #dbeafe, #eff6ff);
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 20px;
    box-shadow: 0 4px 16px rgba(79, 124, 255, 0.12);

    i {
      font-size: 32px;
      color: #4f7cff;
    }
  }

  &__title {
    font-size: 17px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 8px;
  }

  &__desc {
    font-size: 13px;
    color: #64748b;
    margin-bottom: 24px;
    line-height: 1.5;
  }

  &__progress {
    height: 4px;
    background: #e2e8f0;
    border-radius: 4px;
    overflow: hidden;
    margin-bottom: 16px;
  }

  &__bar {
    height: 100%;
    background: linear-gradient(90deg, #4f7cff, #7c3aed);
    border-radius: 4px;
    transition: width 0.4s ease;
  }

  &__tips {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    font-size: 12px;
    color: #94a3b8;
  }

  &__tips-content {
    margin-top: 12px;
    padding: 12px 16px;
    background: #fff;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    text-align: left;

    p {
      font-size: 12px;
      color: #64748b;
      line-height: 1.8;
      margin: 0;
    }
  }
}

/* ========== 协作名称表单 ========== */
.oo-collab-form {
  &__title {
    font-size: 14px;
    font-weight: 600;
    color: #1e293b;
    margin-bottom: 10px;
  }

  &__hint {
    font-size: 12px;
    color: #94a3b8;
    margin-top: 6px;
    line-height: 1.5;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
    margin-top: 14px;
  }
}
</style>
